"use client";

import { BASE_FEE_RATES, SortDirection, TrialDto, USDC_SCALE } from "@decibeltrade/sdk";
import { Button } from "@decibeltrade/web-core/components";
import { useTranslations } from "next-intl";
import { useEffect, useMemo, useState } from "react";
import { css, cx } from "styled-system/css";
import { flex, grid, stack } from "styled-system/patterns";

import { Banner } from "@/components/banner";
import { Skeleton } from "@/components/skeleton";
import { SortableHeader } from "@/components/table/sort";
import { Tag } from "@/components/tag";
import { useTrialHistoryInfiniteQuery } from "@/contexts/queries/funded-trial/trial-history";
import { useMarkets } from "@/contexts/queries/markets";
import { marketPriceFor, useAllMarketPrices } from "@/contexts/queries/prices";
import { linkStyles } from "@/recipes/link";
import { addressesEqual } from "@/utils/address";
import { formatDateTime } from "@/utils/format";
import { NumberFormatter } from "@/utils/number-formatter";
import { getServerTime } from "@/utils/server-time";

import { trialDuration } from "./trial-duration";

const DOTS_PER_SIDE = 10;
const PCT_PER_DOT = 10;

export interface SettleAction {
  pending: boolean;
  onSettle: () => void;
  /** True once this tab already submitted a settle — the CTA is the finalize leg. */
  resubmit: boolean;
}

interface TrialPositionsTableProps {
  trial: TrialDto;
  /** "settled" opens on Trade History, shows no open position, and omits the chart. */
  variant?: "active" | "settled";
  /** User-side keeper backup, shown next to "Settling…" once expired. */
  settle?: SettleAction;
  positionFlat?: boolean;
  /** Submitted settle stalled (live residual) — reassure instead of a dead CTA. */
  settleStalled?: boolean;
}

/**
 * Active-trial body — Positions/Trade History tabs, a
 * single position row (contract allows one active trial), and the cannot-close
 * footnote. History rows come from the settled `protected_trials` list.
 */
export function TrialPositionsTable({
  trial,
  variant = "active",
  settle,
  positionFlat = false,
  settleStalled = false,
}: TrialPositionsTableProps) {
  const t = useTranslations("firstTradeOnUs.details.table");
  const tOverlay = useTranslations("firstTradeOnUs.details.overlay");
  const tStalled = useTranslations("firstTradeOnUs.details.stalled");
  const isSettled = variant === "settled";
  const [tab, setTab] = useState<"positions" | "history">(isSettled ? "history" : "positions");

  const historyQuery = useTrialHistoryInfiniteQuery({ account: trial.user });
  const history = useMemo(() => {
    const rows = (historyQuery.data?.pages ?? [])
      .flatMap((page) => page.history)
      .filter((row) => row.status !== "Active");
    // The just-settled trial belongs in history even before the indexer
    // catches up — seed it from the prop we already hold.
    if (isSettled && !rows.some((row) => row.trial_id === trial.trial_id)) {
      return [trial, ...rows];
    }
    return rows;
  }, [historyQuery.data, isSettled, trial]);
  // The seeded just-settled row can outrun the server count.
  const historyTotal = Math.max(
    historyQuery.data?.pages[0]?.historyTotalCount ?? 0,
    history.length,
  );

  return (
    <div
      className={stack({ gap: "16", w: "full" })}
      data-testid="fft-overlay"
      data-side={trial.side}
    >
      {settleStalled && trial.protected_amount_usd !== undefined && (
        <div className={css({ px: "16" })} data-testid="fft-settle-stalled-banner">
          <Banner variant="warning" className={css({ bg: "amber.900" })}>
            <div className={stack({ gap: "4" })}>
              <strong>{tStalled("badge")}</strong>
              <span>
                {tStalled("explainer", {
                  amount: NumberFormatter.usdc(trial.protected_amount_usd),
                })}
              </span>
            </div>
          </Banner>
        </div>
      )}
      <div className={stack({ gap: "0", w: "full" })}>
        <div className={tabRowStyles}>
          <TabButton active={tab === "positions"} onClick={() => setTab("positions")}>
            {t("positionsTab", { count: isSettled ? 0 : 1 })}
          </TabButton>
          <TabButton active={tab === "history"} onClick={() => setTab("history")}>
            {t("historyTab", { count: historyTotal })}
          </TabButton>
        </div>

        {/* Both panels stay mounted, stacked in one grid cell — the region
            keeps the max height of the two, so switching tabs never resizes
            the chart below (ResponsiveContainer redraws jaggedly on reflow). */}
        <div className={panelGrid}>
          <div className={cx(panelCell, tab !== "positions" && panelHidden)}>
            {isSettled ? (
              <p className={emptyText}>{t("positionsEmpty")}</p>
            ) : (
              <PositionsPanel
                trial={trial}
                expiredLabel={tOverlay("expired")}
                settle={settle}
                positionFlat={positionFlat}
              />
            )}
          </div>
          <div className={cx(panelCell, tab !== "history" && panelHidden)}>
            <HistoryPanel
              history={history}
              totalCount={historyTotal}
              hasNextPage={historyQuery.hasNextPage}
              isFetchingNextPage={historyQuery.isFetchingNextPage}
              onLoadMore={() => void historyQuery.fetchNextPage()}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Tabbed Positions/Trade History body for the trial-less steps, once any
 * history exists — without it a settled trial disappears from the page as
 * soon as a claim or a new lock resets the step. With no history it renders
 * `emptyFallback` (the step's "No trades yet" / hero block) instead.
 */
export function TrialHistorySection({
  account,
  emptyFallback,
  lockCountdown,
}: {
  account?: string;
  emptyFallback?: React.ReactNode;
  lockCountdown?: string | null;
}) {
  const t = useTranslations("firstTradeOnUs.details.table");
  const tDashboard = useTranslations("firstTradeOnUs");
  const [tab, setTab] = useState<"positions" | "history">("history");
  const historyQuery = useTrialHistoryInfiniteQuery({ account });
  const history = useMemo(
    () =>
      (historyQuery.data?.pages ?? [])
        .flatMap((page) => page.history)
        .filter((row) => row.status !== "Active"),
    [historyQuery.data],
  );
  const historyTotal = Math.max(
    historyQuery.data?.pages[0]?.historyTotalCount ?? 0,
    history.length,
  );

  if (history.length === 0) {
    return emptyFallback ?? null;
  }

  return (
    <div className={stack({ gap: "0", w: "full" })} data-testid="fft-history-section">
      <div className={tabRowStyles}>
        <TabButton active={tab === "positions"} onClick={() => setTab("positions")}>
          {t("positionsTab", { count: 0 })}
        </TabButton>
        <TabButton active={tab === "history"} onClick={() => setTab("history")}>
          {t("historyTab", { count: historyTotal })}
        </TabButton>
      </div>
      <div className={panelGrid}>
        <div className={cx(panelCell, tab !== "positions" && panelHidden)}>
          <p className={emptyText}>{t("positionsEmpty")}</p>
        </div>
        <div className={cx(panelCell, tab !== "history" && panelHidden)}>
          <div className={historyScrollStyles}>
            <HistoryPanel
              history={history}
              totalCount={historyTotal}
              hasNextPage={historyQuery.hasNextPage}
              isFetchingNextPage={historyQuery.isFetchingNextPage}
              onLoadMore={() => void historyQuery.fetchNextPage()}
            />
          </div>
        </div>
      </div>
      {lockCountdown && (
        <p className={sectionCountdownStyles} data-testid="fft-history-countdown">
          {tDashboard("timeRemaining", { time: lockCountdown })}
        </p>
      )}
    </div>
  );
}

const sectionCountdownStyles = css({
  textStyle: "body.md",
  color: "tag.text.gold",
  textAlign: "center",
  py: "12",
});

// ~5 rows + the showing-count footer; the step's hero/empty state below keeps its room.
const historyScrollStyles = css({ maxH: "[280px]", overflowY: "auto" });

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      role="tab"
      aria-selected={active}
      className={cx(tabBase, active ? tabActive : tabInactive)}
    >
      {children}
    </button>
  );
}

// Headers + the single position row share this grid; columns hug content.
const tableGrid = grid({
  gridTemplateColumns: "[repeat(8, max-content)]",
  columnGap: "24",
  rowGap: "16",
  px: "16",
  py: "12",
  alignItems: "center",
  minW: "[880px]",
});

const tableScroll = css({ overflowX: "auto", w: "full" });

const panelCell = css({ gridArea: "[1/1]", minW: "0" });
const panelHidden = css({ visibility: "hidden" });

const headerCell = css({ textStyle: "body.md", color: "text.tertiary" });
const valueCell = css({ textStyle: "body.md", color: "text.primary" });
const positiveValueCell = css({ textStyle: "body.md", color: "text.positive" });
const negativeValueCell = css({ textStyle: "body.md", color: "text.negative" });
const tertiaryValueCell = css({ textStyle: "body.md", color: "text.tertiary" });

const panelGrid = css({ display: "grid" });
const rowContents = css({ display: "contents" });
const mutedText = css({ color: "text.tertiary" });
const emptyText = css({ textStyle: "body.md", color: "text.tertiary", px: "16", py: "24" });

const tabRowStyles = flex({
  gap: "24",
  px: "16",
  borderBottomWidth: "100",
  borderBottomStyle: "solid",
  borderBottomColor: "border.primary",
});

const tabBase = css({
  py: "12",
  cursor: "pointer",
  borderBottomWidth: "100",
  borderBottomStyle: "solid",
  borderBottomColor: "transparent",
  mb: "[-1px]",
});
const tabActive = css({
  textStyle: "highlight.md",
  color: "text.secondary",
  borderBottomColor: "text.secondary",
});
const tabInactive = css({ textStyle: "body.md", color: "text.tertiary" });

const pnlCell = css({ textStyle: "body.md" });
const countdownCell = css({ textStyle: "highlight.md", color: "text.secondary" });
const liveDot = css({ w: "[6px]", h: "[6px]", rounded: "full", bg: "text.positive" });

const settleNowButton = css({
  textStyle: "highlight.md",
  color: "text.primary",
  textDecoration: "underline",
  textUnderlineOffset: "2",
  cursor: "pointer",
  _hover: { textDecoration: "none" },
  _disabled: { color: "text.disabled", cursor: "default" },
});

const footnote = css({
  textStyle: "body.md",
  color: "text.disabled",
  px: "16",
  py: "16",
  borderTopWidth: "100",
  borderTopStyle: "solid",
  borderTopColor: "border.primary",
});

function PositionsPanel({
  trial,
  expiredLabel,
  settle,
  positionFlat,
}: {
  trial: TrialDto;
  expiredLabel: string;
  settle?: SettleAction;
  positionFlat: boolean;
}) {
  const t = useTranslations("firstTradeOnUs.details.table");
  const tOverlay = useTranslations("firstTradeOnUs.details.overlay");
  const tGeneral = useTranslations("general");

  const { data: markets } = useMarkets();
  const market = markets.find((m) => addressesEqual(m.market_addr, trial.market));
  const baseAsset = market?.market_name.split("/")[0] ?? "";

  const { data: allPrices } = useAllMarketPrices();
  const livePx =
    trial.market !== undefined ? marketPriceFor(allPrices, trial.market)?.mark_px : undefined;

  // Position confirmed closed: hold the last live mark so PnL/mark/dots stop
  // implying live exposure while the settle finalizes.
  const [lastLivePx, setLastLivePx] = useState<number | undefined>(undefined);
  if (!positionFlat && livePx !== undefined && !Object.is(livePx, lastLivePx)) {
    setLastLivePx(livePx);
  }
  const marketPx = positionFlat ? (lastLivePx ?? livePx) : livePx;

  const sizeDisplay = trial.size;
  const leverage = trialLeverage(trial) ?? 0;
  const isLong = trial.side === "Buy";

  const livePnlUsd = useMemo(() => {
    if (marketPx === undefined || sizeDisplay === null || trial.mark_at_open === undefined) {
      return null;
    }
    const entryPx = trial.mark_at_open / USDC_SCALE;
    const grossPnl = isLong
      ? (marketPx - entryPx) * sizeDisplay
      : (entryPx - marketPx) * sizeDisplay;
    const estFees = BASE_FEE_RATES.taker * (entryPx + marketPx) * sizeDisplay;
    return grossPnl - estFees;
  }, [marketPx, trial.mark_at_open, isLong, sizeDisplay]);
  const pnlPct =
    livePnlUsd !== null &&
    trial.protected_amount_usd !== undefined &&
    trial.protected_amount_usd > 0
      ? (livePnlUsd / trial.protected_amount_usd) * 100
      : null;

  const secondsRemaining = useCountdownSeconds(trial.expires_at_ms ?? null);

  return (
    <div className={stack({ gap: "0", w: "full" })}>
      {/* The countdown/settle column sits ~880px deep in the scroller — keep it reachable on phones. */}
      <div className={mobileCountdownRow}>
        <span className={headerCell}>{t("timeRemaining")}</span>
        <span className={countdownCell}>
          <CountdownValue
            secondsRemaining={secondsRemaining}
            expiredLabel={expiredLabel}
            settle={settle}
          />
        </span>
      </div>
      <div className={tableScroll}>
        <div className={tableGrid}>
          <span className={headerCell}>{t("coinSide")}</span>
          <span className={headerCell}>{t("size")}</span>
          <span className={headerCell}>{t("entryPrice")}</span>
          <span className={headerCell}>{t("markPrice")}</span>
          <span className={headerCell}>{t("estPnl")}</span>
          <span className={headerCell}>{t("principal")}</span>
          <span className={headerCell}>{t("winnersLosers")}</span>
          <span className={headerCell}>{t("timeRemaining")}</span>

          <span className={flex({ gap: "8", align: "center" })}>
            <span className={valueCell}>{t("coinLeverage", { coin: baseAsset, leverage })}</span>
            {trial.side !== undefined && (
              <Tag variant={isLong ? "positive" : "negative"}>
                {isLong ? tOverlay("sideLong") : tOverlay("sideShort")}
              </Tag>
            )}
          </span>
          <span className={valueCell}>
            {sizeDisplay === null
              ? tGeneral("loadingPlaceholder")
              : t("sizeValue", {
                  size: NumberFormatter.size(sizeDisplay, { szDecimals: market?.sz_decimals }),
                  coin: baseAsset,
                })}
          </span>
          <span className={valueCell}>
            {trial.mark_at_open_usd === undefined
              ? tGeneral("loadingPlaceholder")
              : NumberFormatter.usdc(trial.mark_at_open_usd)}
          </span>
          <span className={valueCell}>
            {marketPx === undefined
              ? tGeneral("loadingPlaceholder")
              : NumberFormatter.usdc(marketPx)}
          </span>
          <span className={pnlCell} data-testid="fft-live-pnl">
            <PnlText pnlUsd={livePnlUsd} pnlPct={pnlPct} />
          </span>
          <span className={valueCell}>
            {trial.protected_amount_usd === undefined
              ? tGeneral("loadingPlaceholder")
              : NumberFormatter.usdc(trial.protected_amount_usd)}
          </span>
          <WinnersLosersStrip pnlPct={pnlPct} />
          <span className={countdownCell} data-testid="fft-countdown">
            <CountdownValue
              secondsRemaining={secondsRemaining}
              expiredLabel={expiredLabel}
              settle={settle}
              settleTestId="fft-settle-now"
            />
          </span>
        </div>
      </div>
    </div>
  );
}

const mobileCountdownRow = flex({
  hideFrom: "desktop",
  gap: "8",
  align: "center",
  justify: "space-between",
  px: "16",
  pt: "12",
});

function CountdownValue({
  secondsRemaining,
  expiredLabel,
  settle,
  settleTestId,
}: {
  secondsRemaining: number;
  expiredLabel: string;
  settle?: SettleAction;
  settleTestId?: string;
}) {
  const t = useTranslations("firstTradeOnUs.details.table");
  if (secondsRemaining > 0) {
    return (
      <span className={flex({ gap: "8", align: "center" })}>
        <span className={liveDot} />
        {formatMmSs(secondsRemaining)}
      </span>
    );
  }
  return (
    <span className={flex({ gap: "8", align: "center" })}>
      <span className={mutedText}>{expiredLabel}</span>
      {settle && (
        <button
          type="button"
          onClick={settle.onSettle}
          disabled={settle.pending}
          data-testid={settleTestId}
          className={settleNowButton}
        >
          {t(settle.resubmit ? "finalize" : "settleNow")}
        </button>
      )}
    </span>
  );
}

function PnlText({ pnlUsd, pnlPct }: { pnlUsd: number | null; pnlPct: number | null }) {
  const tGeneral = useTranslations("general");
  if (pnlUsd === null) {
    return <span className={mutedText}>{tGeneral("loadingPlaceholder")}</span>;
  }
  const color = pnlUsd > 0 ? "text.positive" : pnlUsd < 0 ? "text.negative" : "text.primary";
  const pct =
    pnlPct === null
      ? ""
      : ` (${NumberFormatter.percent(pnlPct, { isFraction: false, fixedPrecision: true, explicitPlus: true })})`;
  return (
    <span
      className={css({ color })}
    >{`${NumberFormatter.usdc(pnlUsd, { explicitPlus: true })}${pct}`}</span>
  );
}

const dotBase = css({ w: "4", h: "4", rounded: "full" });
const dotWin = css({ bg: "text.positive" });
const dotLoss = css({ bg: "text.negative" });
const dotOff = css({ bg: "darkBrown.400" });

/**
 * Bipolar gauge of the CURRENT position's PnL: a "Loser" half and a "Winner"
 * half, 10 dots each, lighting from the center outward.
 * One dot per 10% of principal; a maxed side means ±100%+.
 */
function WinnersLosersStrip({ pnlPct }: { pnlPct: number | null }) {
  const lit =
    pnlPct === null ? 0 : Math.min(DOTS_PER_SIDE, Math.ceil(Math.abs(pnlPct) / PCT_PER_DOT));
  const winning = (pnlPct ?? 0) > 0;
  return (
    <span className={flex({ gap: "4", align: "center", flexWrap: "nowrap" })}>
      {Array.from({ length: DOTS_PER_SIDE * 2 }, (_, i) => {
        // Distance from the center seam: loser half counts right-to-left.
        const isWinnerSide = i >= DOTS_PER_SIDE;
        const distFromCenter = isWinnerSide ? i - DOTS_PER_SIDE : DOTS_PER_SIDE - 1 - i;
        const isLit = distFromCenter < lit && (isWinnerSide ? winning : !winning && lit > 0);
        return (
          <span
            key={i}
            className={cx(dotBase, isLit ? (isWinnerSide ? dotWin : dotLoss) : dotOff)}
          />
        );
      })}
    </span>
  );
}

/** Notional / principal; null when a degraded row lacks the inputs. */
function trialLeverage(t: TrialDto): number | null {
  if (
    t.size === null ||
    t.mark_at_open_usd === undefined ||
    t.protected_amount_usd === undefined ||
    t.protected_amount_usd <= 0
  ) {
    return null;
  }
  return Math.round((t.size * t.mark_at_open_usd) / t.protected_amount_usd);
}

function HistoryPanel({
  history,
  totalCount,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: {
  history: TrialDto[];
  totalCount: number;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
}) {
  const t = useTranslations("firstTradeOnUs.details.table");
  const tOverlay = useTranslations("firstTradeOnUs.details.overlay");
  const tGeneral = useTranslations("general");
  const { data: markets } = useMarkets();

  const [timeSort, setTimeSort] = useState<SortDirection>("DESC");
  const sortedHistory = useMemo(() => {
    if (timeSort === undefined) return history;
    const sign = timeSort === "DESC" ? -1 : 1;
    return history.toSorted((a, b) => sign * ((a.closed_at_ms ?? 0) - (b.closed_at_ms ?? 0)));
  }, [history, timeSort]);

  if (history.length === 0) {
    return <p className={emptyText}>{t("historyEmpty")}</p>;
  }

  return (
    <div className={cx(tableScroll)}>
      {/* Headers and rows share ONE grid (rows join via display:contents) so
          the max-content tracks are computed over headers AND cells together. */}
      <div className={historyGrid}>
        <span className={headerCell}>{t("market")}</span>
        <span className={headerCell}>{t("side")}</span>
        <span className={headerCell}>{t("principal")}</span>
        <span className={headerCell}>{t("direction")}</span>
        <span className={headerCell}>{t("outcome")}</span>
        <span className={headerCell}>{t("profitLoss")}</span>
        <span className={headerCell}>
          <SortableHeader label={t("time")} value={timeSort} onValueChange={setTimeSort} />
        </span>
        {sortedHistory.map((row) => {
          const marketName =
            markets.find((m) => addressesEqual(m.market_addr, row.market))?.market_name ??
            tGeneral("loadingPlaceholder");
          const sideKnown = row.side !== undefined;
          const isLong = row.side === "Buy";
          const leverage = trialLeverage(row) ?? 0;
          const payout = row.user_payout_usd ?? 0;
          const openLabel = isLong ? tOverlay("sideLong") : tOverlay("sideShort");
          const closeLabel = openLabel;
          return (
            <div key={row.trial_id} className={rowContents}>
              <span className={valueCell}>{marketName}</span>
              <span
                className={
                  sideKnown ? (isLong ? positiveValueCell : negativeValueCell) : tertiaryValueCell
                }
              >
                {sideKnown
                  ? t("sideLeverage", { leverage, side: openLabel })
                  : tGeneral("loadingPlaceholder")}
              </span>
              <span className={valueCell}>
                {row.protected_amount_usd === undefined
                  ? tGeneral("loadingPlaceholder")
                  : NumberFormatter.usdc(row.protected_amount_usd)}
              </span>
              <span className={valueCell}>
                {sideKnown
                  ? row.mark_at_close_usd != null
                    ? t("directionValue", { open: openLabel, close: closeLabel })
                    : t("directionOpenOnly", { open: openLabel })
                  : tGeneral("loadingPlaceholder")}
              </span>
              <span className={flex()}>
                <Tag variant={payout > 0 ? "positive" : "secondary"}>
                  {payout > 0 ? t("outcomeProfit") : t("outcomeProtected")}
                </Tag>
              </span>
              <span className={payout > 0 ? positiveValueCell : tertiaryValueCell}>
                {NumberFormatter.usdc(payout)}
              </span>
              <span className={valueCell}>
                {row.closed_at_ms != null
                  ? formatDateTime(row.closed_at_ms)
                  : tGeneral("loadingPlaceholder")}
              </span>
            </div>
          );
        })}
      </div>
      <div className={loadMoreRow}>
        <span className={mutedText}>
          {t("showingCount", { shown: history.length, total: totalCount })}
        </span>
        {hasNextPage && (
          <Button
            variant="text"
            onClick={onLoadMore}
            disabled={isFetchingNextPage}
            className={linkStyles}
          >
            {tGeneral("loadMore")}
          </Button>
        )}
      </div>
    </div>
  );
}

const loadMoreRow = flex({ justify: "center", align: "center", gap: "12", pb: "12" });

// Columns hug their content; leftover space stays right.
const historyGrid = grid({
  gridTemplateColumns: "[repeat(7, max-content)]",
  columnGap: "24",
  rowGap: "16",
  px: "16",
  py: "12",
  alignItems: "center",
  minW: "[880px]",
});

function useCountdownSeconds(expiresAtMs: number | null): number {
  const [now, setNow] = useState(() => getServerTime());
  useEffect(() => {
    const id = window.setInterval(() => setNow(getServerTime()), 500);
    return () => window.clearInterval(id);
  }, []);
  if (expiresAtMs === null) return 0;
  return Math.max(0, Math.floor((expiresAtMs - now) / 1000));
}

export function TrialFootnote({ trialMinutes }: { trialMinutes: number }) {
  const t = useTranslations("firstTradeOnUs.details.table");
  const duration = trialDuration(trialMinutes);
  return (
    <p className={footnote}>
      {duration.unit === "hour"
        ? t("footnoteHours", { hours: duration.value })
        : t("footnote", { minutes: duration.value })}
    </p>
  );
}

export function TrialPositionsTableSkeleton() {
  const t = useTranslations("firstTradeOnUs.details.table");

  return (
    <div className={stack({ gap: "16", w: "full" })} data-testid="fft-trial-positions-skeleton">
      <div className={stack({ gap: "0", w: "full" })}>
        <div className={tabRowStyles}>
          <span className={cx(tabBase, tabActive)}>{t("positionsTab", { count: 1 })}</span>
          <span className={cx(tabBase, tabInactive)}>{t("historyTab", { count: 0 })}</span>
        </div>
        <div className={tableScroll}>
          <div className={tableGrid}>
            <span className={headerCell}>{t("coinSide")}</span>
            <span className={headerCell}>{t("size")}</span>
            <span className={headerCell}>{t("entryPrice")}</span>
            <span className={headerCell}>{t("markPrice")}</span>
            <span className={headerCell}>{t("estPnl")}</span>
            <span className={headerCell}>{t("principal")}</span>
            <span className={headerCell}>{t("winnersLosers")}</span>
            <span className={headerCell}>{t("timeRemaining")}</span>

            <Skeleton className={css({ w: "[80px]", h: "[16px]", rounded: "200" })} />
            <Skeleton className={css({ w: "[60px]", h: "[16px]", rounded: "200" })} />
            <Skeleton className={css({ w: "[70px]", h: "[16px]", rounded: "200" })} />
            <Skeleton className={css({ w: "[70px]", h: "[16px]", rounded: "200" })} />
            <Skeleton className={css({ w: "[80px]", h: "[16px]", rounded: "200" })} />
            <Skeleton className={css({ w: "[60px]", h: "[16px]", rounded: "200" })} />
            <Skeleton className={css({ w: "[120px]", h: "[16px]", rounded: "200" })} />
            <Skeleton className={css({ w: "[60px]", h: "[16px]", rounded: "200" })} />
          </div>
        </div>
      </div>
    </div>
  );
}

export function formatMmSs(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const mmSs = `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return h > 0 ? `${h}:${mmSs}` : mmSs;
}
